import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef
} from '@angular/core';
import {
  AxisTickStrategies,
  DateTimeTickStrategy,
  emptyTick,
  LineSeries,
  SeriesXY,
} from '@arction/lcjs';
import { ChartsCommonService } from '../../../services/charts-common.service';
import { CommonChartComponent } from '../common-chart/common-chart.component';
import {
  AxisXHistorical,
  IMappedSeriesSingleContractAnalyzerPoints,
  IMappedSeriesPointsResponse,
  Point,
  PointsResponseType,
  PointType
} from 'app/charts-dashboard/interfaces/chart-http.interface';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries
} from 'app/charts-dashboard/state/series';
import { ChartsDashboardCardQuery } from 'app/charts-dashboard/state/card';
import { ChartsDashboardQuery } from 'app/charts-dashboard/state/dashboard';
import { zip } from 'rxjs';
import { ChartsHttpService } from 'app/charts-dashboard/services/charts-http.service';
import {
  EntityActions,
  ID
} from '@datorama/akita';
import {
  ChartTypeEnum,
  Datetime,
  IChartFilter,
  IHistoricalPointsData,
  ILCPoint,
  RealTimeEnum,
  TimeRange
} from 'app/charts-dashboard/interfaces';
import { CommonService } from 'app/core/services/common.service';
import {
  finalize,
  takeUntil
} from 'rxjs/operators';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';

@Component({
  selector: 'iv-live-historical-chart',
  template: '<ng-content></ng-content>',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class HistoricalChartComponent extends CommonChartComponent {

  constructor(
    protected readonly commonService: CommonService,
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly elementRef: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dashboardQuery: ChartsDashboardQuery,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    protected readonly cardsQuery: ChartsDashboardCardQuery,
    protected readonly httpService: ChartsHttpService,
    protected readonly chartDashboardState: ChartsDashboardStateService
  ) {
    super(commonService, chartsCommonService, elementRef, changeDetectorRef, dashboardQuery, seriesQuery, cardsQuery, chartDashboardState);
  }

  protected originDate: Date;

  protected loadSeriesPoints(seriesIds: ID[], isFirstLoad?: boolean): void {
    const series: IChartSeries[] = this.seriesQuery.getAll().filter((_series: IChartSeries) => seriesIds.find(id => id === _series.id));

    zip(...this.createSeriesObservables(series, isFirstLoad))
      .pipe(finalize(() => {
        if (this.card.chartId === ChartTypeEnum.SPOT_CHART && this.mainSeries.selectedFilters.vModeToggler) {
          this.addSeriesVWap();
        }
      }))
      .subscribe((res: IMappedSeriesPointsResponse<AxisXHistorical>[]) => {
        res.forEach((pointsData: IMappedSeriesPointsResponse<AxisXHistorical>) => {
          if (isFirstLoad && this.chart.getDefaultAxisX().getTickStrategy() === 'Numeric' && pointsData.seriesId === this.mainSeries.id) {
            this.setTickStrategy(pointsData.points);
          }
          this.fillLineSeries(pointsData, this.mainSeries?.id === pointsData.seriesId, isFirstLoad);
          this.pointsData = {
            ...this.pointsData,
            [pointsData.seriesId]: pointsData
          };
        });
      });
  }

  protected loadAllSeriesPoints(isFirstLoad?: boolean): void {
    zip(...this.createSeriesObservables(this.allSeries, true))
      .pipe(finalize(() => {
        if (this.card.chartId === ChartTypeEnum.SPOT_CHART && this.mainSeries.selectedFilters.vModeToggler) {
          this.addSeriesVWap();
        }
      }))
      .subscribe((res: IMappedSeriesPointsResponse<AxisXHistorical>[]) => {
        if (isFirstLoad) {
          this.setTickStrategy(
            res.find((pointsData: IMappedSeriesPointsResponse<AxisXHistorical>) => pointsData.seriesId === this.mainSeries.id).points
          );
          this.setCursorLegendAxis(res[0]);
        }

        res.forEach((pointsData: IMappedSeriesPointsResponse<AxisXHistorical>, index: number) => {
          this.fillLineSeries(pointsData, !index, isFirstLoad);
          this.pointsData = {
            ...this.pointsData,
            [pointsData.seriesId]: pointsData
          };
        });
      });
  }

  // Функция установки стратегии для оси X (Ось с датами)
  protected setTickStrategy(mainSeriesPoints: PointType[]): void {
    if (!mainSeriesPoints.length) return;

    // Тут мы берем первую точку из mainSeries и извлекаем из нее дату, по это дате мы понимаем, где у нас левая грань графика
    this.setOriginDate(mainSeriesPoints[0]);

    // Совсем отключили скролл в самом чарте
    // this.chart.setMouseInteractionWheelZoom(false);

    this.chart
      .getDefaultAxisX()
      .setTickStrategy(
        AxisTickStrategies.DateTime,
        (tickStrategy: DateTimeTickStrategy) => tickStrategy
          .setDateOrigin(this.originDate)
          .setGreatTickStyle(emptyTick)
          .setFormattingHour(
            null,
            {minute: '2-digit', hour: '2-digit'},
            {minute: '2-digit', hour: '2-digit'}
          )
          .setFormattingDay(
            null,
            {day: 'numeric', month: 'long'},
            {hour: '2-digit', minute: '2-digit'})
          .setFormattingWeek(
            null,
            {day: 'numeric', month: 'long'},
            {day: 'numeric'}
          )
      );
  }

  protected setOriginDate(firstMainSeriesPoint: PointType): void {
    this.originDate = new Date((firstMainSeriesPoint as Point<AxisXHistorical>).x);
  }

  // TODO: 491-я задача реализует этот метод
  // protected setAxisYInterval(): void {
  // }

  protected mapPoints(pointsData: PointsResponseType): ILCPoint[] {
    if (!this.originDate && pointsData.points.length) {
      this.setOriginDate(pointsData[0]);
    }

    const timeRangeKey = this.getTimeRangeKey()

    return (pointsData.points as Point<AxisXHistorical>[]).map((item: Point<AxisXHistorical>) => {
      const timeRange: TimeRange = this.mainSeries.selectedFilters[timeRangeKey].name;
      const x =
        timeRange.includes('M') ||
        timeRange.includes('Y') ||
        timeRange.includes('ALL')
          ? item.x + 'T00:00-0600'
          : item.x;
      return {x: new Date(x).getTime() - this.originDate.getTime(), y: +item.y, z: item?.z};
    });
  }

  protected setCursorResultTableFormatter(lineSeries: LineSeries, series: IChartSeries, filters?: IChartFilter[], doNotShowAxisZ?: boolean): void {
    if (!lineSeries) return;

    lineSeries
      .setCursorResultTableFormatter((builder, _series: SeriesXY, x: number, y: number, dataPoint: ILCPoint) => {
        this.builderCursorResult = builder;
        const dateTimeRow = this.getDateTimeRow(_series,x)
        this.builderCursorResult
        .addRow(`${this.cursorResult.x === 'Date' ? '' : this.cursorResult.x} ${dateTimeRow}`.replace(',', ''))
        .addRow(`${series.stock.symbol}`)
        .addRow(`${this.cursorResult.y}: ${Math.round((y + Number.EPSILON) * 100) / 100}`);


        if (dataPoint.z && !doNotShowAxisZ) {
          this.builderCursorResult.addRow(`${this.cursorResult.z}: ${dataPoint.z}`);
        }

        if (filters?.length) {
          filters.forEach((filter: IChartFilter) => {
            this.builderCursorResult.addRow(`${filter.name}: ${series.selectedFilters[filter.parameterId].description}`);
          });
        }

        return this.builderCursorResult;
      });
  }

  // Добавляем точки в конец графика, когда включен rt режим
  protected addPointsFromRt(data: PointsResponseType): void {
    if (!data.points.length) return;

    // Проверка на одинаковые точки. Если у нас уже есть эта точка - мы ее не добавляем
    if (
      data.points.length === 1 &&
      (data as IMappedSeriesPointsResponse<AxisXHistorical>).points[0].x === (this.pointsData as IHistoricalPointsData)[data.seriesId]
        .points[(this.pointsData as IHistoricalPointsData)[data.seriesId].points.length - 1].x
    ) {
      return;
    }

    this.lineSeries[data.seriesId].add(this.mapPoints(data));
    this.pointsData = {
      ...this.pointsData,
      [data.seriesId]: {
        ...this.pointsData[data.seriesId],
        points: [
          ...(this.pointsData as IHistoricalPointsData)[data.seriesId].points,
          ...data.points
        ]
      }
    } as IHistoricalPointsData;
  }

  protected getFrom(series: IChartSeries): Datetime | undefined {
    if (!series) return;

    return (this.pointsData as IHistoricalPointsData)[series.id].points && series.selectedFilters.realtime?.name === RealTimeEnum.ON
      ? (this.pointsData as IHistoricalPointsData)
        [series.id]
        .points[(this.pointsData as IHistoricalPointsData)[series.id].points.length - 1]
        ?.x
        .toString()
        .replace('T', ' ').slice(0, 19) as Datetime
      : undefined;
  }

  protected seriesSubscription(): void {
    super.seriesSubscription();
    this.seriesQuery.selectEntityAction(EntityActions.Update)
      .pipe(takeUntil(this.destroy$))
      .subscribe((ids: ID[]) => {
        if (ids.filter(id => this.card.chartSeries.includes(id)).length) {
          this.loadSeriesPoints(ids, true);
        }

        if (this.mainSeries.selectedFilters.realtime?.name === RealTimeEnum.ON) {
          this.startRt();
        } else if (this.rtSubscription) {
          this.rtSubscription.unsubscribe();
          this.isRtActive = false;
        }
      });
  }

  private getTimeRangeKey():string {
    return this.card.chartId === ChartTypeEnum.HISTORICAL_VOLATILITY || this.card.chartId === ChartTypeEnum.TOTAL_OPEN_INTEREST_VOLUME
    ? 'eodTimeRange' : 'timeRange';
  }

  protected getDateTimeRow(series: SeriesXY, x: number): string {
    if (
      this.mainSeries.selectedFilters[this.getTimeRangeKey()].name.includes("M") ||
      this.mainSeries.selectedFilters[this.getTimeRangeKey()].name.includes("Y") ||
      this.mainSeries.selectedFilters[this.getTimeRangeKey()].name.includes("ALL")
    ) {
      return series.axisX.formatValue(x).substring(0, 10);
    } else return series.axisX.formatValue(x);
  }

  protected addSeriesVWap(): void {}
}
